import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useParams } from "react-router-dom";
import "./FormProyecto.css";

const EditarProyecto = () => {
  const { id } = useParams();
  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    ubicacion: "",
    fecha_inicio: "",
    fecha_fin: "",
    estado: "activo",
    presupuesto: "",
    presupuesto_gastado: "",
    progreso: 0,
    responsable: "",
    prioridad: "media",
    notas: ""
  });

  const [errores, setErrores] = useState({});
  const [loading, setLoading] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje] = useState(null);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [accionConfirmacion, setAccionConfirmacion] = useState(null);

  const cargar = async () => {
    try {
      setCargando(true);
      const res = await api.get(`/proyectos/${id}`);
      setForm(res.data);
    } catch (error) {
      console.error("Error cargando proyecto:", error);
      setMensaje({
        tipo: "error",
        texto: "Error al cargar el proyecto"
      });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Limpiar error del campo al escribir
    if (errores[name]) {
      setErrores({ ...errores, [name]: "" });
    }
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!form.nombre || form.nombre.trim().length < 3) {
      nuevosErrores.nombre = "El nombre debe tener al menos 3 caracteres";
    }

    if (!form.fecha_inicio) {
      nuevosErrores.fecha_inicio = "La fecha de inicio es obligatoria";
    }

    if (form.fecha_fin && form.fecha_inicio) {
      if (new Date(form.fecha_fin) < new Date(form.fecha_inicio)) {
        nuevosErrores.fecha_fin = "La fecha de fin debe ser posterior a la de inicio";
      }
    }

    if (form.presupuesto && parseFloat(form.presupuesto) < 0) {
      nuevosErrores.presupuesto = "El presupuesto no puede ser negativo";
    }

    if (form.presupuesto_gastado && parseFloat(form.presupuesto_gastado) < 0) {
      nuevosErrores.presupuesto_gastado = "El presupuesto gastado no puede ser negativo";
    }

    if (form.progreso < 0 || form.progreso > 100) {
      nuevosErrores.progreso = "El progreso debe estar entre 0 y 100";
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const guardarCambios = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      setMensaje({
        tipo: "error",
        texto: "Por favor, corrige los errores en el formulario"
      });
      return;
    }

    setLoading(true);
    setMensaje(null);

    try {
      const datosProyecto = {
        ...form,
        presupuesto: form.presupuesto ? parseFloat(form.presupuesto) : 0,
        presupuesto_gastado: form.presupuesto_gastado ? parseFloat(form.presupuesto_gastado) : 0,
        progreso: parseInt(form.progreso) || 0
      };

      await api.put(`/proyectos/${id}`, datosProyecto);
      
      setMensaje({
        tipo: "exito",
        texto: "Proyecto actualizado exitosamente"
      });

      // Recargar datos
      setTimeout(() => {
        cargar();
      }, 1000);
    } catch (error) {
      console.error("Error actualizando proyecto:", error);
      
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.detalles?.[0] ||
                      "Error al actualizar el proyecto";
      
      setMensaje({
        tipo: "error",
        texto: errorMsg
      });
    } finally {
      setLoading(false);
    }
  };

  const cambiarEstado = async (nuevoEstado) => {
    try {
      setLoading(true);
      await api.patch(`/proyectos/${id}/estado`, { estado: nuevoEstado });
      
      setMensaje({
        tipo: "exito",
        texto: `Estado cambiado a "${nuevoEstado}"`
      });

      // Actualizar el estado localmente
      setForm({ ...form, estado: nuevoEstado });
      setMostrarConfirmacion(false);
    } catch (error) {
      console.error("Error cambiando estado:", error);
      setMensaje({
        tipo: "error",
        texto: "Error al cambiar el estado"
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmarCambioEstado = (nuevoEstado) => {
    const mensajes = {
      pausado: "¿Deseas pausar este proyecto?",
      activo: "¿Deseas reactivar este proyecto?",
      completado: "¿Deseas marcar este proyecto como completado?",
      cancelado: "¿Deseas cancelar este proyecto? Esta acción es importante."
    };

    setAccionConfirmacion({
      estado: nuevoEstado,
      mensaje: mensajes[nuevoEstado]
    });
    setMostrarConfirmacion(true);
  };

  const cancelar = () => {
    if (window.confirm("¿Estás seguro de salir? Los cambios no guardados se perderán.")) {
      window.location.href = "/proyectos";
    }
  };

  const getEstadoColor = (estado) => {
    const colores = {
      activo: "#10b981",
      pausado: "#f59e0b",
      completado: "#3b82f6",
      cancelado: "#ef4444"
    };
    return colores[estado] || "#6b7280";
  };

  if (cargando) {
    return (
      <div className="form-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando proyecto...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <div>
          <h1>Editar Proyecto</h1>
          <p className="subtitle">Actualiza la información del proyecto</p>
        </div>
        <button onClick={cancelar} className="btn-cerrar" title="Cerrar">
          ✕
        </button>
      </div>

      {/* Estado actual */}
      <div className="estado-actual">
        <span className="label">Estado actual:</span>
        <span
          className="badge-grande"
          style={{ backgroundColor: getEstadoColor(form.estado) }}
        >
          {form.estado}
        </span>
        
        {/* Botones de cambio de estado */}
        <div className="cambio-estado-btns">
          {form.estado !== "activo" && (
            <button
              onClick={() => confirmarCambioEstado("activo")}
              className="btn-estado activo"
              disabled={loading}
            >
              Reactivar
            </button>
          )}
          {form.estado === "activo" && (
            <button
              onClick={() => confirmarCambioEstado("pausado")}
              className="btn-estado pausado"
              disabled={loading}
            >
              Pausar
            </button>
          )}
          {(form.estado === "activo" || form.estado === "pausado") && (
            <>
              <button
                onClick={() => confirmarCambioEstado("completado")}
                className="btn-estado completado"
                disabled={loading}
              >
                Completar
              </button>
              <button
                onClick={() => confirmarCambioEstado("cancelado")}
                className="btn-estado cancelado"
                disabled={loading}
              >
                Cancelar
              </button>
            </>
          )}
        </div>
      </div>

      {mensaje && (
        <div className={`mensaje ${mensaje.tipo}`}>
          <span className="mensaje-icon">
            {mensaje.tipo === "exito" ? "✓" : "⚠"}
          </span>
          <span>{mensaje.texto}</span>
        </div>
      )}

      <form onSubmit={guardarCambios} className="proyecto-form">
        {/* Información básica */}
        <div className="form-section">
          <h3 className="section-title">Información Básica</h3>
          
          <div className="form-row">
            <div className="form-group full">
              <label htmlFor="nombre">
                Nombre del Proyecto <span className="required">*</span>
              </label>
              <input
                id="nombre"
                name="nombre"
                type="text"
                placeholder="Nombre del proyecto"
                value={form.nombre}
                onChange={handleChange}
                className={errores.nombre ? "error" : ""}
                disabled={loading}
              />
              {errores.nombre && <span className="error-text">{errores.nombre}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full">
              <label htmlFor="descripcion">Descripción</label>
              <textarea
                id="descripcion"
                name="descripcion"
                placeholder="Describe el proyecto..."
                value={form.descripcion || ""}
                onChange={handleChange}
                rows="4"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ubicacion">Ubicación</label>
              <input
                id="ubicacion"
                name="ubicacion"
                type="text"
                placeholder="Ubicación del proyecto"
                value={form.ubicacion || ""}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="responsable">Responsable</label>
              <input
                id="responsable"
                name="responsable"
                type="text"
                placeholder="Nombre del responsable"
                value={form.responsable || ""}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Fechas y planificación */}
        <div className="form-section">
          <h3 className="section-title">Fechas y Planificación</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fecha_inicio">
                Fecha de Inicio <span className="required">*</span>
              </label>
              <input
                id="fecha_inicio"
                name="fecha_inicio"
                type="date"
                value={form.fecha_inicio}
                onChange={handleChange}
                className={errores.fecha_inicio ? "error" : ""}
                disabled={loading}
              />
              {errores.fecha_inicio && (
                <span className="error-text">{errores.fecha_inicio}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="fecha_fin">Fecha de Finalización</label>
              <input
                id="fecha_fin"
                name="fecha_fin"
                type="date"
                value={form.fecha_fin || ""}
                onChange={handleChange}
                className={errores.fecha_fin ? "error" : ""}
                disabled={loading}
                min={form.fecha_inicio}
              />
              {errores.fecha_fin && (
                <span className="error-text">{errores.fecha_fin}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="prioridad">Prioridad</label>
              <select
                id="prioridad"
                name="prioridad"
                value={form.prioridad}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="baja">🟢 Baja</option>
                <option value="media">🟡 Media</option>
                <option value="alta">🟠 Alta</option>
                <option value="urgente">🔴 Urgente</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="progreso">Progreso (%)</label>
              <input
                id="progreso"
                name="progreso"
                type="number"
                value={form.progreso}
                onChange={handleChange}
                className={errores.progreso ? "error" : ""}
                disabled={loading}
                min="0"
                max="100"
              />
              {errores.progreso && (
                <span className="error-text">{errores.progreso}</span>
              )}
              <div className="progreso-visual">
                <div
                  className="progreso-fill"
                  style={{
                    width: `${form.progreso || 0}%`,
                    backgroundColor: getEstadoColor(form.estado)
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Presupuesto */}
        <div className="form-section">
          <h3 className="section-title">Presupuesto</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="presupuesto">Presupuesto Total (RD$)</label>
              <input
                id="presupuesto"
                name="presupuesto"
                type="number"
                placeholder="0.00"
                value={form.presupuesto || ""}
                onChange={handleChange}
                className={errores.presupuesto ? "error" : ""}
                disabled={loading}
                min="0"
                step="0.01"
              />
              {errores.presupuesto && (
                <span className="error-text">{errores.presupuesto}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="presupuesto_gastado">Presupuesto Gastado (RD$)</label>
              <input
                id="presupuesto_gastado"
                name="presupuesto_gastado"
                type="number"
                placeholder="0.00"
                value={form.presupuesto_gastado || ""}
                onChange={handleChange}
                className={errores.presupuesto_gastado ? "error" : ""}
                disabled={loading}
                min="0"
                step="0.01"
              />
              {errores.presupuesto_gastado && (
                <span className="error-text">{errores.presupuesto_gastado}</span>
              )}
            </div>
          </div>

          {form.presupuesto > 0 && (
            <div className="presupuesto-info">
              <div className="info-item">
                <span>Disponible:</span>
                <strong>
                  RD$ {(parseFloat(form.presupuesto || 0) - parseFloat(form.presupuesto_gastado || 0)).toFixed(2)}
                </strong>
              </div>
              <div className="info-item">
                <span>Utilizado:</span>
                <strong>
                  {((parseFloat(form.presupuesto_gastado || 0) / parseFloat(form.presupuesto || 1)) * 100).toFixed(1)}%
                </strong>
              </div>
            </div>
          )}
        </div>

        {/* Notas adicionales */}
        <div className="form-section">
          <h3 className="section-title">Notas Adicionales</h3>
          
          <div className="form-row">
            <div className="form-group full">
              <label htmlFor="notas">Notas</label>
              <textarea
                id="notas"
                name="notas"
                placeholder="Información adicional, observaciones, etc."
                value={form.notas || ""}
                onChange={handleChange}
                rows="4"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            onClick={cancelar}
            className="btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <span className="icon">💾</span>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal de confirmación */}
      {mostrarConfirmacion && (
        <div className="modal-overlay" onClick={() => setMostrarConfirmacion(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmar Acción</h3>
            <p>{accionConfirmacion?.mensaje}</p>
            <div className="modal-actions">
              <button
                onClick={() => setMostrarConfirmacion(false)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={() => cambiarEstado(accionConfirmacion?.estado)}
                className="btn-primary"
                disabled={loading}
              >
                {loading ? "Procesando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditarProyecto;